import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Building, Check, ChevronDown, Crown, Lock, Share2, Store, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Text } from '@/components/ui/Text'
import { organizationsApi, populationsApi } from '@/lib/api'
import type { OrgMember } from '@/lib/api'
import { useAuth } from '@/lib/config/auth-provider'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { UserAvatar } from '@/components/ui/UserAvatar'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { MemberSearchInput, type OrgMemberOption } from '@/components/sharing/MemberSearchInput'
import { ShareModalShell } from '@/components/sharing/ShareModalShell'
import {
  GeneralAccessSelect,
  ShareModalFooter,
  ShareAccessMode,
  ShareAccessOption,
} from '@/components/sharing/ShareAccessSections'
import type { useAudienceSharing, SharingMode } from '../hooks/useAudienceSharing'
import type { AudienceAttributionMode } from '@/types/components/audiences/audience-sharing'
import MarketplaceRemoveDialog from './MarketplaceRemoveDialog'
import { ExternalInvitePanel } from './ExternalInvitePanel'
import { AttributionPanel } from './AttributionPanel'

interface ShareAudienceDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  audienceId: string
  audienceName: string
  sharing: ReturnType<typeof useAudienceSharing>
  onDeleteAudience?: () => void
  onAudienceChanged?: () => void | Promise<void>
  canPublishMarketplace?: boolean
  initialSharedAs?: AudienceAttributionMode
  initialSharedAsUserId?: string | null
  /** When true, the current viewer is the audience owner and can transfer ownership. */
  isOwner?: boolean
}

type ActiveTab = 'access' | 'distribution' | 'collaborate'
type ExpandedDistribution = 'published' | 'marketplace' | null

interface DistributionOptionProps {
  icon: ReactNode
  label: string
  subtitle: string
  selected: boolean
  disabled?: boolean
  onClick: () => void
  actionLabel?: string
  onAction?: () => void
  testId: string
}

function getInitials(name: string): string {
  return name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2)
}

function DistributionOption(props: DistributionOptionProps) {
  const { icon, label, subtitle, selected, disabled, onClick, actionLabel, onAction, testId } = props

  return (
    <div
      className={cn(
        'flex w-full items-start gap-3 rounded-lg border border-border px-3 py-3 text-left transition-colors',
        'hover:bg-muted/50',
        selected && 'bg-muted/30',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        disabled={disabled}
        className="flex min-w-0 flex-1 items-start gap-3 text-left disabled:cursor-not-allowed"
      >
        <div className="mt-0.5">{icon}</div>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-sm font-medium text-foreground">{label}</span>
          <span className="text-xs text-muted-foreground mt-0.5">{subtitle}</span>
        </div>
      </button>
      {selected && onAction && actionLabel ? (
        <button
          type="button"
          data-testid="stop-partner-sharing"
          onClick={onAction}
          disabled={disabled}
          className="mt-0.5 shrink-0 text-xs font-medium text-destructive hover:underline disabled:opacity-50"
        >
          {actionLabel}
        </button>
      ) : selected ? (
        <Check className="w-4 h-4 text-foreground mt-0.5 shrink-0" strokeWidth={2} />
      ) : null}
    </div>
  )
}

export default function ShareAudienceDialog({
  open,
  onOpenChange,
  audienceId,
  audienceName,
  sharing,
  onDeleteAudience,
  onAudienceChanged,
  canPublishMarketplace,
  initialSharedAs,
  initialSharedAsUserId,
  isOwner,
}: ShareAudienceDialogProps) {
  const { t } = useTranslation('app')
  const { user } = useAuth()
  const { org } = useCurrentOrg()
  const orgId = user?.organizationToken

  const [activeTab, setActiveTab] = useState<ActiveTab>('access')
  const [members, setMembers] = useState<Array<OrgMemberOption>>([])
  const [rawMembers, setRawMembers] = useState<Array<OrgMember>>([])
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const [linkCopied, setLinkCopied] = useState(false)
  const [expandedDistribution, setExpandedDistribution] = useState<ExpandedDistribution>(null)
  const [transferDialogOpen, setTransferDialogOpen] = useState(false)
  const [transferTarget, setTransferTarget] = useState<OrgMemberOption | null>(null)
  const [transferring, setTransferring] = useState(false)

  useEffect(() => {
    if (!open || !orgId) return
    organizationsApi.getMembers(orgId).then((raw: Array<OrgMember>) => {
      setRawMembers(raw)
      setMembers(raw.map((m) => ({
        userId: m.id,
        name: m.name || m.email || t('unknown', { ns: 'common' }),
        email: m.email || '',
        initials: getInitials(m.name || m.email || '??'),
        avatarSeed: null,
      })))
    }).catch(() => {})
  }, [open, orgId])

  useEffect(() => {
    if (!open) return
    if (sharing.sharingMode === 'published') {
      setExpandedDistribution('published')
      return
    }
    if (sharing.sharingMode === 'marketplace') {
      setExpandedDistribution('marketplace')
      return
    }
    setExpandedDistribution(null)
  }, [open, sharing.sharingMode])

  const accessMode: ShareAccessMode = useMemo(() => {
    if (sharing.sharingMode === 'organization') return 'organization'
    return 'private'
  }, [sharing.sharingMode])

  const accessOptions: Array<ShareAccessOption> = useMemo(() => [
    { value: 'private', label: t('sharing.onlyMe'), description: 'Only you can use this audience', icon: Lock },
    { value: 'organization', label: t('sharing.organization'), description: 'Everyone at your org can view', icon: Building },
  ], [t])

  const excludedUserIds = sharing.collaborators.map((c) => c.userId)
  const isPublished = sharing.sharingMode === 'published'
  const isMarketplace = sharing.sharingMode === 'marketplace'
  const audienceShareUrl = accessMode === 'private' || typeof window === 'undefined'
    ? null
    : `${window.location.origin}/audiences/${audienceId}`

  async function handleSelectMode(mode: SharingMode) {
    if (mode === sharing.sharingMode) return
    if (sharing.sharingMode === 'marketplace' && mode !== 'marketplace') {
      setRemoveDialogOpen(true)
      return
    }
    await sharing.updateMode(mode)
  }

  async function handleSelectAccessMode(mode: ShareAccessMode) {
    await handleSelectMode(mode)
  }

  async function handleSelectPartnerDistribution() {
    if (isPublished) {
      setExpandedDistribution((current) => current === 'published' ? null : 'published')
      return
    }
    await handleSelectMode('published')
    setExpandedDistribution('published')
  }

  async function handleStopPartnerSharing() {
    const ok = await sharing.updateMode('organization')
    if (!ok) return
    setExpandedDistribution(null)
    await onAudienceChanged?.()
  }

  async function handleAddCollaborator(member: OrgMemberOption) {
    await sharing.addCollaborator(member.userId)
  }

  async function handleUpdateCollaboratorRole(collaboratorId: string, role: 'collaborator' | 'manager') {
    const ok = await sharing.updateCollaboratorRole(collaboratorId, role)
    if (ok) toast.success(t('sharing.roleUpdated'))
  }

  const handleTransferOwnership = useCallback(async () => {
    if (!transferTarget) return
    setTransferring(true)
    try {
      await populationsApi.updateOwnership(audienceId, { changeOwnerTo: transferTarget.userId })
      toast.success(t('sharing.ownershipTransferred', { name: transferTarget.name }))
      setTransferDialogOpen(false)
      setTransferTarget(null)
      onOpenChange(false)
      await onAudienceChanged?.()
    } catch {
      toast.error(t('sharing.transferFailed'))
    } finally {
      setTransferring(false)
    }
  }, [audienceId, transferTarget, onOpenChange, onAudienceChanged, t])

  async function handleCopyLink() {
    if (!audienceShareUrl) return
    try {
      await navigator.clipboard.writeText(audienceShareUrl)
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2000)
      toast.success(t('sharing.linkCopied'))
    } catch {
      toast.error(t('sharing.copyFailed'))
    }
  }

  const handleDeactivate = useCallback(async () => {
    try {
      await populationsApi.updateMarketplaceStatus(audienceId, 'inactive')
      onOpenChange(false)
      await onAudienceChanged?.()
    } catch {
      toast.error(t('sharing.marketplaceRemoveFailed'))
    }
  }, [audienceId, onAudienceChanged, onOpenChange])

  const handleMakePrivate = useCallback(async () => {
    const ok = await sharing.updateMode('private')
    if (!ok) return
    onOpenChange(false)
    await onAudienceChanged?.()
  }, [sharing, onAudienceChanged, onOpenChange])

  const handleDeleteFromRemoveDialog = useCallback(() => {
    onOpenChange(false)
    onDeleteAudience?.()
  }, [onOpenChange, onDeleteAudience])

  return (
    <>
      <MarketplaceRemoveDialog
        open={removeDialogOpen}
        onOpenChange={setRemoveDialogOpen}
        audienceId={audienceId}
        audienceName={audienceName}
        onDeactivate={handleDeactivate}
        onMakePrivate={handleMakePrivate}
        onDelete={handleDeleteFromRemoveDialog}
      />

      {/* Transfer ownership confirmation */}
      <AlertDialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <AlertDialogContent className="sm:max-w-[420px]">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('sharing.transferOwnershipQ')}</AlertDialogTitle>
            <AlertDialogDescription>
              {transferTarget
                ? `${transferTarget.name || transferTarget.email} will become the new owner of "${audienceName}".`
                : `The selected user will become the new owner of "${audienceName}".`}
              {' '}
              <strong>You will lose edit access unless you are re-added as a collaborator.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setTransferDialogOpen(false)}>{t('cancel', { ns: 'common' })}</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleTransferOwnership}
              disabled={transferring}
              data-testid="transfer-ownership-confirm"
            >
              {transferring ? t('transferring', { ns: 'common' }) : t('transfer', { ns: 'common' })}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ShareModalShell
        open={open}
        onOpenChange={onOpenChange}
        title={t('sharing.shareAudience')}
        tabs={[
          { id: 'access', label: t('sharing.access') },
          { id: 'distribution', label: t('sharing.distribution') },
          { id: 'collaborate', label: t('sharing.collaborate') },
        ]}
        activeTab={activeTab}
        onTabChange={(tabId) => setActiveTab(tabId as ActiveTab)}
        footer={activeTab === 'access' ? (
          <ShareModalFooter
            shareUrl={audienceShareUrl}
            saving={sharing.saving}
            copied={linkCopied}
            onCopy={handleCopyLink}
            onDone={() => onOpenChange(false)}
          />
        ) : null}
      >
        {activeTab === 'access' && (
          <div className="flex flex-col gap-0">
            <div className="flex flex-col gap-3 px-6 py-5">
              <span className="text-xs font-medium text-muted-foreground">
                {t('sharing.generalAccess')}
              </span>
              <GeneralAccessSelect
                value={accessMode}
                options={accessOptions}
                onChange={handleSelectAccessMode}
                disabled={sharing.saving}
              />
              {isPublished && (
                <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 dark:border-emerald-800 dark:bg-emerald-950/30">
                  <Share2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                  <span className="text-sm text-emerald-700 dark:text-emerald-300">
                    Partner distribution active
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'distribution' && (
          <div className="flex flex-col gap-5 px-6 py-5">
            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium text-muted-foreground">{t('sharing.partnerDistribution')}</span>
              <DistributionOption
                icon={<Share2 className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />}
                label={t('sharing.partnerAccounts')}
                subtitle={t('sharing.partnerAccountsHint')}
                selected={isPublished}
                disabled={sharing.saving}
                onClick={handleSelectPartnerDistribution}
                actionLabel="Stop"
                onAction={handleStopPartnerSharing}
                testId="share-radio-published"
              />
              {isPublished && expandedDistribution === 'published' && (
                <div
                  data-testid="external-invite-panel"
                  className="flex flex-col gap-3 animate-in fade-in slide-in-from-top-1 duration-200"
                >
                  <ExternalInvitePanel audienceId={audienceId} />
                  <AttributionPanel
                    audienceId={audienceId}
                    members={rawMembers}
                    organizationName={org?.name ?? null}
                    currentOwnerId={user?.id}
                    initialSharedAs={initialSharedAs ?? 'member'}
                    initialSharedAsUserId={initialSharedAsUserId ?? null}
                  />
                </div>
              )}
            </div>

            {canPublishMarketplace && (
              <div className="flex flex-col gap-2">
                <span className="text-xs font-medium text-muted-foreground">{t('sharing.marketplace')}</span>
                <DistributionOption
                  icon={<Store className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />}
                  label={t('audienceEdit.publishMarketplace')}
                  subtitle={t('sharing.marketplaceHint')}
                  selected={isMarketplace}
                  disabled={sharing.saving}
                  onClick={() => handleSelectMode('marketplace')}
                  testId="share-radio-marketplace"
                />
              </div>
            )}
          </div>
        )}

        {activeTab === 'collaborate' && (
          <div className="flex flex-col gap-4 px-6 py-5">
            <Text size="sm" color="muted">
              Add people who can manage this audience
            </Text>

            <MemberSearchInput
              members={members}
              excludeUserIds={excludedUserIds}
              onSelect={handleAddCollaborator}
            />

            <div className="flex flex-col gap-1">
              {user && (
                <div className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2.5">
                    <UserAvatar
                      size="xs"
                      initials={getInitials(user.name || user.email || '?')}
                      name={user.name || user.email || '?'}
                    />
                    <span className="text-sm text-foreground">
                      {user.name || user.email || t('unknown', { ns: 'common' })}
                      <span className="text-muted-foreground"> (You)</span>
                    </span>
                  </div>
                  <span className="text-sm text-muted-foreground">{t('owner', { ns: 'common' })}</span>
                </div>
              )}
              {sharing.collaborators
                .filter((c) => c.role !== 'owner' && c.source !== 'external_invite')
                .map((collab) => (
                  <div key={collab.id} className="flex items-center justify-between py-1.5">
                    <div className="flex items-center gap-2.5">
                      <UserAvatar
                        size="xs"
                        initials={getInitials(collab.name || collab.email || '??')}
                        name={collab.name || collab.email || '?'}
                      />
                      <span className="text-sm text-foreground truncate">
                        {collab.name || collab.email || t('unknown', { ns: 'common' })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <select
                          value={collab.role === 'manager' ? 'manager' : 'collaborator'}
                          onChange={(e) => handleUpdateCollaboratorRole(collab.id, e.target.value as 'collaborator' | 'manager')}
                          className="appearance-none rounded border border-border bg-background py-0.5 pl-2 pr-6 text-sm text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                        >
                          <option value="collaborator">{t('sharing.viewRole')}</option>
                          <option value="manager">{t('sharing.manageRole')}</option>
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
                      </div>
                      <button
                        type="button"
                        onClick={() => sharing.removeCollaborator(collab.id)}
                        className="text-muted-foreground hover:text-destructive transition-colors p-0.5"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
            </div>

            {isOwner && members.length > 0 && (
              <div className="border-t border-border pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Crown className="h-4 w-4 text-muted-foreground" />
                    <Text size="sm" color="muted">{t('sharing.transferOwnership')}</Text>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1">
                    <MemberSearchInput
                      members={members.filter((m) => m.userId !== user?.id)}
                      excludeUserIds={transferTarget ? [transferTarget.userId] : []}
                      onSelect={(m) => setTransferTarget(m)}
                      placeholder={t('sharing.selectNewOwner')}
                    />
                  </div>
                  {transferTarget && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setTransferDialogOpen(true)}
                      data-testid="transfer-ownership-open-confirm"
                    >
                      {t('transfer', { ns: 'common' })}
                    </Button>
                  )}
                </div>
                {transferTarget && (
                  <Text size="xs" color="muted" className="mt-1">
                    Transferring to {transferTarget.name || transferTarget.email}
                  </Text>
                )}
              </div>
            )}
          </div>
        )}
      </ShareModalShell>
    </>
  )
}
